/* eslint-disable @next/next/no-img-element */
// 用户账号列表：个人账号 + 团队账号

import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import IconFont from '$/components/IconFont';
import useTeamStore, { clearTeamInfo, getTeamRoleText, setTeamInfo } from '@/store/useTeamStore';
import { TeamListItem } from '@/domains/team/types';
import { navigate, getEnv } from '@/common/agents';
import { useOfficialStore } from '$/store/useOfficialStore';
import { teamSwitch } from '@/domains/team/teamService';
import { useLanguageStore } from '$/store/languageStore';
import TeamPackageBadge from '@/components/TeamPackageBadge';
import style from './index.module.scss';

/** 用户最多可创建的团队数量 */
const MAX_TEAM_COUNT = 5;

interface UserTeamAccountListProps {
  teamList: TeamListItem[];
  onCreateTeam?: () => void;
}

interface AccountAvatarProps {
  src: string;
}

/**
 * 账号头像，加载失败时展示占位背景。
 */
function AccountAvatar({ src }: AccountAvatarProps) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError || !src) {
    return <div className={cn(style.avatar, style.avatarPlaceholder)} aria-hidden />;
  }

  return (
    <img
      src={src}
      alt=""
      className={style.avatar}
      crossOrigin="anonymous"
      onError={() => setHasError(true)}
    />
  );
}

/**
 * 团队账户下拉列表，展示个人账号、团队账号与创建团队入口。
 */
function UserTeamAccountList({
  teamList,
  onCreateTeam,
}: UserTeamAccountListProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const teamInfo = useTeamStore((state) => state.teamInfo);
  const userInfo = useOfficialStore((state) => state.userInfo);

  const homeUrl = getEnv().ENV === 'development' ? '/--roboneo/home' : '/home';

  /**
   * 切换为个人账号：清空团队信息并跳转首页。
   */
  const onSelectPersonal = async () => {
    if (!teamInfo) {
      return;
    }
    try {
      trackTeamSwitchClick('switch_personal');
      const res = await teamSwitch(0);
      // 当前是团队版才能且为个人账号，如果当前已经为个人账号则不需要切换
      if (res.identity === 'personal' && !!teamInfo) {
        clearTeamInfo();
        navigate({
          mode: 'url',
          url: homeUrl,
          replace: true,
        });
      }
    } catch (error) {
      console.error('切换失败', error);
    }
  };

  // 切换团队
  const onSelectTeam = async (oid: number) => {
    try {
      if (teamInfo?.oid === oid) {
        return;
      }
      const curTeamInfo = teamList.find((item) => item.oid === oid);
      if (curTeamInfo) {
        const res = await teamSwitch(curTeamInfo.oid);
        if (res.identity === 'team') {
          setTeamInfo(curTeamInfo);
          navigate({
            mode: 'url',
            url: homeUrl,
            replace: true,
          });
        }
      } else {
        console.error('切换失败');
      }
    } catch (error) {
      console.error('切换失败', error);
    }
  };

  const trackTeamSwitchClick = (click_type: 'open_team' | 'switch_team' | 'switch_personal', oid?: number) => {
    const subscribe = useOfficialStore.getState().userInfo?.subscribe;
    const { radishAmount } = useOfficialStore.getState();
    window.mtstat?.track('roboneo_team_team_switch_click', {
      vip_level: subscribe?.level,
      meiye_num: radishAmount,
      click_type,
      oid: oid || '',
    });
  };

  return (
    <div className={style.container}>
      <div className={style.sectionHeader}>
        <IconFont name="userV3Bold" fontSize={16} color="var(--content-primary)" />
        <span className={style.sectionTitle}>
          {languageStore.smartcatTranslationText((v) => v.personal_account)}
        </span>
      </div>

      <div
        className={style.accountItem}
        role="button"
        tabIndex={0}
        onClick={onSelectPersonal}
      >
        <div className={style.accountInfo}>
          <AccountAvatar src={userInfo.avatar} />
          <span
            className={style.personalNickname}
          >
            {userInfo.screenName}
          </span>
        </div>
      </div>

      <div className={style.dividerWrapper}>
        <div className={style.divider} />
      </div>

      <div className={style.sectionHeader}>
        <IconFont name="userGroupV3Bold" fontSize={16} color="var(--content-primary)" />
        <span className={style.sectionTitle}>
          {languageStore.smartcatTranslationText((v) => v.team_account)}
        </span>
      </div>

      <div className={style.teamList}>
        {teamList.map((team: TeamListItem) => {
          return (
            <div
              key={team.oid}
              className={style.teamAccountItem}
              role="button"
              tabIndex={0}
              onClick={() => {
                trackTeamSwitchClick('switch_team', team.oid);
                onSelectTeam?.(team.oid);
              }}
            >
              <div className={style.accountInfo}>
                <AccountAvatar src={team.avatar_url} />
                <div className={style.middleInfo}>
                  <span
                    className={style.nickname}
                  >
                    {team.name}
                  </span>
                  <div className={style.tag_box}>
                    <TeamPackageBadge teamInfo={team} className={style.teamLevel} />
                    <div
                      className={cn(style.roleBadge, {
                        [style.admin]: team.role === 1,
                        [style.member]: team.role === 2,
                      })}
                    >
                      {getTeamRoleText(team)}
                    </div>
                  </div>
                </div>

                {/* 选中态ICON */}
                {teamInfo?.oid === team.oid && <IconFont name="checkBlack" fontSize={16} color="var(--content-secondary)" />}

              </div>

            </div>
          );
        })}
      </div>

      {teamList.length < MAX_TEAM_COUNT && (
        <>
          <div className={style.dividerWrapper}>
            <div className={style.divider} />
          </div>

          <div
            className={style.createTeamItem}
            role="button"
            tabIndex={0}
            onClick={onCreateTeam}
          >
            <IconFont name="plusBold" fontSize={16} color="var(--content-secondary)" />
            <span>
              {languageStore.smartcatTranslationText((v) => v.create_a_team)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default UserTeamAccountList;
